import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const SuperAdminContext = createContext(null);

const SUPERADMIN_TOKEN_KEY = 'superadmin_token';
const SUPERADMIN_USER_KEY = 'superadmin_user';

export const SuperAdminProvider = ({ children }) => {
  const [superAdmin, setSuperAdmin] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(SUPERADMIN_TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchSuperAdminProfile();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSuperAdminProfile = async () => {
    try {
      const response = await api.get('/superadmin/auth/me');

      if (response.data.success) {
        setSuperAdmin(response.data.data);
        localStorage.setItem(SUPERADMIN_USER_KEY, JSON.stringify(response.data.data));
      } else {
        // Token invalid, clear storage
        logout();
      }
    } catch (error) {
      console.error('Error fetching superadmin profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('Attempting SuperAdmin login for:', email);
      const response = await api.post('/superadmin/auth/login', { email, password });
      console.log('SuperAdmin login response:', response.data);

      if (response.data.success) {
        // Handle both 'token' and 'accessToken' for backward compatibility
        const newToken = response.data.data.token || response.data.data.accessToken;
        const admin = response.data.data.admin || response.data.data.user || response.data.data.superAdmin;

        if (!newToken) {
          console.error('No token in response:', response.data);
          return { success: false, message: 'No authentication token received' };
        }

        setToken(newToken);
        setSuperAdmin(admin);

        localStorage.setItem(SUPERADMIN_TOKEN_KEY, newToken);
        localStorage.setItem(SUPERADMIN_USER_KEY, JSON.stringify(admin));

        return { success: true, admin };
      }

      return { success: false, message: response.data.message || 'Login failed' };
    } catch (error) {
      console.error('SuperAdmin login error:', error);
      console.error('Error response:', error.response?.data);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  };

  const logout = () => {
    setToken(null);
    setSuperAdmin(null);
    localStorage.removeItem(SUPERADMIN_TOKEN_KEY);
    localStorage.removeItem(SUPERADMIN_USER_KEY);
  };

  const hasPermission = (permission) => {
    if (!superAdmin) return false;
    if (superAdmin.role === 'super_admin') return true;
    return superAdmin.permissions?.includes(permission) || false;
  };

  const value = {
    superAdmin,
    token,
    loading,
    isAuthenticated: !!token && !!superAdmin,
    login,
    logout,
    hasPermission,
    isSuperAdmin: superAdmin?.role === 'super_admin',
    isSupport: superAdmin?.role === 'support',
    isBilling: superAdmin?.role === 'billing'
  };

  return (
    <SuperAdminContext.Provider value={value}>
      {children}
    </SuperAdminContext.Provider>
  );
};

export const useSuperAdmin = () => {
  const context = useContext(SuperAdminContext);
  if (!context) {
    throw new Error('useSuperAdmin must be used within a SuperAdminProvider');
  }
  return context;
};

export default SuperAdminContext;